import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Table,
  Loader,
  Center,
  Paper,
  Alert,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconFileText,
  IconCalendar,
  IconCheck,
  IconClock,
  IconX,
  IconEye,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { bidSubmissionApi } from '../../../api/bidSubmissions';
import { BidSubmission } from '../../../api/bidSubmissions';

export function VendorSubmissions() {
  const navigate = useNavigate();
  const [selectedSubmission, setSelectedSubmission] = useState<BidSubmission | null>(null);

  const { data: submissions, isLoading, error } = useQuery<BidSubmission[]>({
    queryKey: ['vendor-bid-submissions'],
    queryFn: () => bidSubmissionApi.getVendorBidSubmissions(),
  });

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'SUBMITTED':
        return <Badge color="green" leftSection={<IconCheck size={12} />}>Submitted</Badge>;
      case 'SAVED':
        return <Badge color="yellow" leftSection={<IconClock size={12} />}>Draft</Badge>;
      default:
        return <Badge color="gray">Unknown</Badge>;
    }
  };

  const getTenderStageBadge = (stage?: string) => {
    const colors: Record<string, string> = {
      PUBLISHED: 'blue',
      COLLECTION: 'cyan',
      OPENED: 'orange',
      TECH_EVAL: 'purple',
      FIN_EVAL: 'indigo',
      AWARDED: 'green',
      EXPIRED: 'red',
    };
    return <Badge color={colors[stage || ''] || 'gray'}>{stage || 'Unknown'}</Badge>;
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Error">
          Failed to load submissions. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            color="green"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/vendor/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Group>

        <Title order={2} mb="md">
          My Bid Submissions
        </Title>

        {!submissions || submissions.length === 0 ? (
          <Card shadow="sm" p="xl">
            <Stack align="center" gap="md">
              <IconFileText size={48} color="gray" />
              <Text size="lg" c="dimmed">
                You haven't submitted any bids yet.
              </Text>
              <Button
                color="green"
                onClick={() => navigate('/vendor/tenders')}
              >
                Browse Tenders
              </Button>
            </Stack>
          </Card>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tender</Table.Th>
                <Table.Th>Reference No</Table.Th>
                <Table.Th>Total Price</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Tender Stage</Table.Th>
                <Table.Th>Submitted At</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {submissions.map((submission) => (
                <Table.Tr key={submission.id}>
                  <Table.Td>
                    <Text fw={500} lineClamp={2}>
                      {submission.tender?.description || 'N/A'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {submission.tender?.referenceNo || 'N/A'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      ${Number(submission.totalPrice).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Table.Td>
                  <Table.Td>{getStatusBadge(submission.status)}</Table.Td>
                  <Table.Td>{getTenderStageBadge(submission.tender?.stage)}</Table.Td>
                  <Table.Td>
                    {submission.submittedAt ? (
                      <Group gap="xs">
                        <IconCalendar size={14} />
                        <Text size="sm">
                          {dayjs(submission.submittedAt).format('MMM DD, YYYY HH:mm')}
                        </Text>
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">Not submitted</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconEye size={14} />}
                      onClick={() => {
                        if (submission.tender?.id) {
                          navigate(`/vendor/submissions/${submission.tender.id}/${submission.id}`);
                        }
                      }}
                    >
                      View
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Container>
  );
}

